import { useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../api/client.js';
import type { PosPaymentAttempt } from '../../api/pos-types.js';
import { Chip, QueryState, type TabProps } from './common.js';
import { formatDateTime, formatRupees, humanize } from './format.js';

const STATUSES = ['created', 'pending', 'succeeded', 'failed', 'expired', 'cancelled', 'unknown'];

const TONE: Record<string, 'ok' | 'warn' | 'bad' | undefined> = {
  succeeded: 'ok',
  pending: 'warn',
  unknown: 'warn',
  failed: 'bad',
};

export function PaymentsTab({ orgId, outletId }: TabProps): JSX.Element {
  const [status, setStatus] = useState('');
  const attempts = useQuery({
    queryKey: ['pos-payments', orgId, outletId, status],
    queryFn: () =>
      apiFetch<{ attempts: PosPaymentAttempt[] }>(
        `/organizations/${orgId}/outlets/${outletId}/pos/payments${status ? `?status=${status}` : ''}`,
      ),
  });

  return (
    <section aria-labelledby="payments-heading">
      <h2 id="payments-heading">UPI payments</h2>
      <label className="inline-field">
        Status{' '}
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </label>
      <QueryState loading={attempts.isLoading} error={attempts.error} />
      {attempts.data && attempts.data.attempts.length === 0 ? (
        <p className="muted">No payment attempts yet.</p>
      ) : null}
      {attempts.data && attempts.data.attempts.length > 0 ? (
        <div className="table-wrap">
          <table className="pos-table">
            <thead>
              <tr>
                <th scope="col">Started</th>
                <th scope="col">Status</th>
                <th scope="col">Asked</th>
                <th scope="col">Collected</th>
                <th scope="col">Collected twice</th>
                <th scope="col">Held for refunds</th>
                <th scope="col">Needs a look</th>
              </tr>
            </thead>
            <tbody>
              {attempts.data.attempts.map((a) => (
                <tr key={a.id}>
                  <th scope="row">{formatDateTime(a.createdAt)}</th>
                  <td>
                    <Chip tone={TONE[a.status]}>{a.status}</Chip>
                  </td>
                  <td>{formatRupees(a.amountPaise)}</td>
                  <td>{a.capturedPaise === null ? '—' : formatRupees(a.capturedPaise)}</td>
                  <td>{a.overCollectedPaise > 0 ? formatRupees(a.overCollectedPaise) : '—'}</td>
                  <td>{a.refundCommittedPaise > 0 ? formatRupees(a.refundCommittedPaise) : '—'}</td>
                  <td>
                    {a.flags.length === 0 ? (
                      <span className="muted">—</span>
                    ) : (
                      a.flags.map((f) => (
                        <Chip key={f} tone="warn">
                          {humanize(f)}
                        </Chip>
                      ))
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
